import React, { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import JsonLd from "./JsonLd";
import { supabase } from "@/integrations/supabase/client";
import { useForumSettings } from "@/hooks/useForumSettings";
import { useAuth } from "@/hooks/useAuth";
import { htmlToText } from "@/utils/htmlToText";
import {
  generateTopicTitle,
  generateCategoryTitle,
  generateTopicDescription,
  generateCategoryDescription,
  shouldUseAutoGeneration,
  FORUM_NAME,
} from "@/utils/seoHelpers";

interface PageMetadata {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
}

interface MetadataContextType {
  setPageMetadata: (metadata: PageMetadata) => void;
}

const MetadataContext = createContext<MetadataContextType | null>(null);

export const useMetadata = () => {
  const context = useContext(MetadataContext);
  if (!context) {
    throw new Error("useMetadata must be used within MetadataProvider");
  }
  return context;
};

interface MetadataProviderProps {
  children: ReactNode;
}

export const MetadataProvider: React.FC<MetadataProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { getSetting } = useForumSettings();
  const { user } = useAuth();
  const [customMetadata, setCustomMetadata] = React.useState<PageMetadata>({});

  // Get forum settings for SEO
  const forumName = getSetting("forum_name_override", "Minor Soccer Talks");
  const titleSeparator = getSetting("seo_title_separator", " | ");
  const autoGenerationEnabled = getSetting(
    "seo_auto_generate_topic_titles",
    true
  );

  // Get topic metadata if on topic page - using the same approach as useTopicByPath
  const {
    data: topicMetadata,
    isLoading: isTopicLoading,
    error: topicError,
  } = useQuery({
    queryKey: ["topic-metadata", params.categorySlug, params.topicSlug],
    queryFn: async () => {
      if (!params.topicSlug || !params.categorySlug) {
        return null;
      }

      console.log("MetadataProvider: Fetching topic metadata for:", {
        topicSlug: params.topicSlug,
        categorySlug: params.categorySlug,
      });

      try {
        // Step 1: Find the category ID (same as useTopicByPath)
        const { data: category, error: categoryError } = await supabase
          .from("categories")
          .select("id, name")
          .eq("slug", params.categorySlug)
          .single();

        if (categoryError || !category) {
          console.error("MetadataProvider: Category not found:", categoryError);
          return null;
        }

        console.log("MetadataProvider: Found category:", category);

        // Step 2: Find the topic using the category ID
        const { data: topic, error: topicError } = await supabase
          .from("topics")
          .select(
            `
            id,
            title,
            slug,
            meta_title,
            meta_description,
            canonical_url,
            og_title,
            og_description,
            og_image,
            content,
            category_id
          `
          )
          .eq("slug", params.topicSlug)
          .eq("category_id", category.id)
          .single();

        if (topicError || !topic) {
          console.error("MetadataProvider: Topic not found:", topicError);
          return null;
        }

        console.log("MetadataProvider: ✅ Found topic with metadata:", {
          title: (topic as any)?.title,
          meta_title: (topic as any)?.meta_title,
          hasMetaTitle: !!(topic as any)?.meta_title,
        });

        return { ...(topic as any), categories: category };
      } catch (error) {
        console.error("MetadataProvider: Error in topic query:", error);
        return null;
      }
    },
    enabled: !!params.topicSlug && !!params.categorySlug,
  });

  // Get category metadata if on category page (and not topic page)
  const { data: categoryMetadata } = useQuery({
    queryKey: [
      "category-metadata",
      params.categorySlug,
      params.subcategorySlug,
    ],
    queryFn: async () => {
      if (!params.categorySlug || params.topicSlug) return null;

      // Handle hierarchical structure
      const categorySlug = params.subcategorySlug || params.categorySlug;

      const { data, error } = await supabase
        .from("categories")
        .select(
          "name, meta_title, meta_description, meta_keywords, canonical_url, og_title, og_description, og_image"
        )
        .eq("slug", categorySlug)
        .single();

      if (error) {
        console.error("MetadataProvider: Error fetching category:", error);
        return null;
      }

      return data;
    },
    enabled: !!params.categorySlug && !params.topicSlug,
  });

  const setPageMetadata = (metadata: PageMetadata) => {
    setCustomMetadata(metadata);
  };

  // Get blog post metadata for blog post pages
  const { data: blogPostMetadata } = useQuery({
    queryKey: ["blog-post-metadata", params.slug],
    queryFn: async () => {
      if (!params.slug || !location.pathname.startsWith("/blog/")) return null;

      const { data, error } = await supabase
        .from("blog_posts")
        .select(
          `
          title,
          excerpt,
          content,
          featured_image,
          category
        `
        )
        .eq("slug", params.slug)
        .single();

      if (error) {
        console.error("MetadataProvider: Error fetching blog post:", error);
        return null;
      }

      return data;
    },
    enabled: !!params.slug && location.pathname.startsWith("/blog/"),
  });

  // Get user profile data for profile page
  const { data: profileData } = useQuery({
    queryKey: ["profile-metadata", user?.id],
    queryFn: async () => {
      if (!user?.id || location.pathname !== "/profile") return null;

      const { data, error } = await supabase
        .from("profiles")
        .select("username")
        .eq("id", user.id)
        .single();

      if (error) return null;
      return data;
    },
    enabled: !!user?.id && location.pathname === "/profile",
  });

  // Helpers for formatting
  const formatSlug = (slug?: string) => {
    if (!slug) return "";
    return slug
      .split("-")
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ");
  };
  const truncate = (s: string, len = 160) =>
    s.length > len ? s.slice(0, len - 1).trimEnd() + "…" : s;

  // Determine page metadata based on current route
  // Move TopicMetadataType interface to top-level scope
  interface TopicMetadataType {
    id: string;
    title?: string;
    slug?: string;
    meta_title?: string;
    meta_description?: string;
    canonical_url?: string;
    og_title?: string;
    og_description?: string;
    og_image?: string;
    content?: string;
    category_id?: string;
    category_name?: string;
    created_at?: string;
    reply_count?: number;
    categories?: { id: string; name: string };
  }

  const getPageMetadata = (): PageMetadata => {
    // Custom metadata takes highest priority
    if (Object.keys(customMetadata).length > 0) {
      return {
        title: customMetadata.title
          ? `${customMetadata.title}${titleSeparator}${forumName}`
          : undefined,
        ...customMetadata,
      };
    }

    // Topic page metadata - Database first approach
    if (topicMetadata && params.topicSlug) {
      const catName =
        (topicMetadata as TopicMetadataType).category_name ||
        formatSlug(params.subcategorySlug || params.categorySlug || "");

      console.log(
        "🏷️ MetadataProvider: Generating topic title with components:",
        {
          topicTitle: topicMetadata.title,
          categoryName: catName,
          forumName,
          separator: titleSeparator,
          existingMetaTitle: topicMetadata.meta_title,
          autoGenerationEnabled,
        }
      );

      // Use database meta_title if it exists, otherwise auto-generate with all 3 components
      let title;
      if (topicMetadata.meta_title) {
        title = topicMetadata.meta_title;
        console.log("✅ Using database meta_title:", title);
      } else if (autoGenerationEnabled) {
        title = generateTopicTitle({
          topicTitle: topicMetadata.title || formatSlug(params.topicSlug!),
          categoryName: catName,
          forumName,
          separator: titleSeparator,
        });
        console.log("✅ Auto-generated title with all components:", title);
      } else {
        title = `${
          topicMetadata.title || formatSlug(params.topicSlug!)
        }${titleSeparator}${forumName}`;
        console.log("✅ Basic title (auto-generation disabled):", title);
      }

      const description =
        topicMetadata.meta_description ||
        generateTopicDescription({
          topicTitle: topicMetadata.title || formatSlug(params.topicSlug!),
          categoryName: catName,
          forumName,
        });

      return {
        title,
        description,
        canonical: topicMetadata.canonical_url,
        ogTitle: topicMetadata.og_title || title,
        ogDescription: topicMetadata.og_description || description,
        ogImage: topicMetadata.og_image,
      };
    }

    // Fallback while topic/category data loads - only use if query failed or finished loading
    if (params.topicSlug && !isTopicLoading && !topicMetadata) {
      const cat = formatSlug(
        params.subcategorySlug || params.categorySlug || ""
      );
      const t = formatSlug(params.topicSlug);
      const fallbackTitle = generateTopicTitle({
        topicTitle: t,
        categoryName: cat,
        forumName,
        separator: titleSeparator,
      });

      console.log(
        "⚠️ MetadataProvider: Using fallback topic title (query finished, no data):",
        fallbackTitle
      );

      return {
        title: fallbackTitle,
        description: generateTopicDescription({
          topicTitle: t,
          categoryName: cat,
          forumName,
        }),
      };
    }

    // Loading state for topic pages - minimal title to prevent flash
    if (params.topicSlug && isTopicLoading) {
      console.log(
        "⏳ MetadataProvider: Topic query loading, using minimal title"
      );
      return {
        title: forumName,
        description: `Loading topic on ${forumName}...`,
      };
    }

    // Blog post metadata - Database first approach
    if (
      blogPostMetadata &&
      params.slug &&
      location.pathname.startsWith("/blog/")
    ) {
      console.log(
        "📝 MetadataProvider: Generating blog post metadata for:",
        params.slug
      );

      // Auto-generate title since blog posts don't have meta fields
      const title = `${(blogPostMetadata as any).title}${titleSeparator}${forumName}`;

      // Use excerpt, fallback to content excerpt or generated description
      let description = (blogPostMetadata as any).excerpt;
      if (!description && (blogPostMetadata as any).content) {
        const textContent = htmlToText((blogPostMetadata as any).content);
        description = truncate(textContent, 150);
      }
      if (!description) {
        description = `Read "${(blogPostMetadata as any).title}" on ${forumName} blog.`;
      }

      return {
        title,
        description,
        ogTitle: title,
        ogDescription: description,
        ogImage: (blogPostMetadata as any).featured_image,
      };
    }

    // Category page metadata - Database first approach
    if (categoryMetadata && params.categorySlug && !params.topicSlug) {
      const categoryName =
        (categoryMetadata as any).name || formatSlug(params.categorySlug);

      // Use database meta_title if it exists, otherwise auto-generate
      const title =
        (categoryMetadata as any).meta_title ||
        generateCategoryTitle(categoryName, forumName, titleSeparator);
      const description =
        (categoryMetadata as any).meta_description ||
        generateCategoryDescription(categoryName, forumName);

      return {
        title,
        description,
        keywords: (categoryMetadata as any).meta_keywords,
        canonical: (categoryMetadata as any).canonical_url,
        ogTitle: (categoryMetadata as any).og_title || title,
        ogDescription: (categoryMetadata as any).og_description || description,
        ogImage: (categoryMetadata as any).og_image,
      };
    }

    // Fallback while category loads
    if (params.categorySlug && !params.topicSlug) {
      const c = formatSlug(params.categorySlug);
      return {
        title: generateCategoryTitle(c, forumName, titleSeparator),
        description: generateCategoryDescription(c, forumName),
      };
    }

    // Dynamic route-based titles
    const path = location.pathname;
    const searchQuery = searchParams.get("q");

    // Home page
    if (path === "/") {
      return {
        title: getSetting("seo_home_title", forumName),
        description: getSetting(
          "seo_home_description",
          "Join the leading online community for youth soccer players, parents, and coaches."
        ),
        keywords: getSetting(
          "seo_home_keywords",
          "youth soccer, minor soccer, soccer community"
        ),
        canonical: getSetting("seo_home_canonical_url", ""),
        ogTitle: getSetting("seo_home_og_title", ""),
        ogDescription: getSetting("seo_home_og_description", ""),
        ogImage: getSetting("seo_home_og_image", ""),
      };
    }

    // Search page
    if (path === "/search") {
      const title = searchQuery
        ? `Search results for "${searchQuery}"${titleSeparator}${forumName}`
        : `Search${titleSeparator}${forumName}`;
      return {
        title,
        description: searchQuery
          ? `Search results for "${searchQuery}" on ${forumName}`
          : `Search topics and discussions on ${forumName}`,
      };
    }

    // Profile page
    if (path === "/profile") {
      const username = (profileData as any)?.username || (profileData as any)?.display_name || "User";
      return {
        title: `${username}'s Profile${titleSeparator}${forumName}`,
        description: `View ${username}'s profile, posts, and activity on ${forumName}`,
      };
    }

    // Admin pages
    if (path.startsWith("/admin")) {
      const adminSection = path.split("/")[2];
      const sectionTitles: Record<string, string> = {
        users: "User Management",
        content: "Content Management",
        moderation: "Moderation",
        spam: "Spam Management",
        seo: "SEO Settings",
        settings: "Settings",
      };

      const sectionTitle = adminSection
        ? sectionTitles[adminSection] || "Dashboard"
        : "Dashboard";
      return {
        title: `Admin ${sectionTitle}${titleSeparator}${forumName}`,
        description: `Admin panel - ${sectionTitle} for ${forumName}`,
      };
    }

    // Static pages with comprehensive coverage
    const routeTitles: Record<string, { title: string; description: string }> =
      {
        "/topics": {
          title: `All Topics${titleSeparator}${forumName}`,
          description: `Browse all topics and discussions on ${forumName}`,
        },
        "/categories": {
          title: `Categories${titleSeparator}${forumName}`,
          description: `Browse all discussion categories on ${forumName}`,
        },
        "/settings": {
          title: `Account Settings${titleSeparator}${forumName}`,
          description: `Manage your account settings and preferences on ${forumName}`,
        },
        "/login": {
          title: `Login${titleSeparator}${forumName}`,
          description: `Sign in to your ${forumName} account`,
        },
        "/register": {
          title: `Register${titleSeparator}${forumName}`,
          description: `Create a new account on ${forumName}`,
        },
        "/create": {
          title: `Create Topic${titleSeparator}${forumName}`,
          description: `Start a new discussion on ${forumName}`,
        },
        "/terms": {
          title: `Terms of Service${titleSeparator}${forumName}`,
          description: `Terms of service and user agreement for ${forumName}`,
        },
        "/privacy": {
          title: `Privacy Policy${titleSeparator}${forumName}`,
          description: `Privacy policy and data protection information for ${forumName}`,
        },
        "/blog": {
          title: `Blog${titleSeparator}${forumName}`,
          description: `Latest news and updates from ${forumName}`,
        },
        "/blogs": {
          title: `Blog${titleSeparator}${forumName}`,
          description: `Latest news and updates from ${forumName}`,
        },
      };

    // Check for exact route match
    if (routeTitles[path]) {
      return routeTitles[path];
    }

    // Default fallback
    return {
      title: forumName,
      description:
        "Join the leading online community for youth soccer players, parents, and coaches discussing clubs, tryouts, ECNL, MLS Next, and recruiting.",
    };
  };

  const metadata = getPageMetadata();

  // Debug logging for title tracking
  console.log("🏷️ MetadataProvider computed metadata:", metadata);
  console.log("📄 Current document.title:", document.title);
  console.log("🔍 Current pathname:", location.pathname);
  console.log("🔗 URL params:", {
    topicSlug: params.topicSlug,
    categorySlug: params.categorySlug,
    subcategorySlug: params.subcategorySlug,
  });
  console.log("💾 Topic metadata state:", topicMetadata ? "LOADED" : "NULL");
  console.log(
    "💾 Category metadata state:",
    categoryMetadata ? "LOADED" : "NULL"
  );
  console.log(
    "💾 Blog post metadata state:",
    blogPostMetadata ? "LOADED" : "NULL"
  );

  // Self-referencing canonical and og:url fallback — critical so per-page meta isn't
  // attributed to the homepage by crawlers.
  const siteOrigin =
    (getSetting("site_url", "") as string) ||
    (typeof window !== "undefined"
      ? window.location.origin
      : "https://minorsoccertalks.com");
  const canonicalUrl = metadata.canonical || `${siteOrigin}${location.pathname}`;

  return (
    <MetadataContext.Provider value={{ setPageMetadata }}>
      <Helmet>
        {metadata.title && <title>{metadata.title}</title>}
        {metadata.description && (
          <meta name="description" content={metadata.description} />
        )}
        {metadata.keywords && (
          <meta name="keywords" content={metadata.keywords} />
        )}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />

        {/* RSS Feed Discovery */}
        <link
          rel="alternate"
          type="application/rss+xml"
          title={`${forumName} RSS Feed`}
          href="/rss"
        />
        {params.categorySlug && (
          <link
            rel="alternate"
            type="application/rss+xml"
            title={`${forumName} - ${params.categorySlug} RSS Feed`}
            href={`/rss?category=${params.categorySlug}`}
          />
        )}

        {/* Open Graph tags */}
        {metadata.ogTitle && (
          <meta property="og:title" content={metadata.ogTitle} />
        )}
        {metadata.ogDescription && (
          <meta property="og:description" content={metadata.ogDescription} />
        )}
        {metadata.ogImage && (
          <meta property="og:image" content={metadata.ogImage} />
        )}

        {/* Twitter Card tags */}
        {metadata.ogTitle && (
          <meta name="twitter:title" content={metadata.ogTitle} />
        )}
        {metadata.ogDescription && (
          <meta name="twitter:description" content={metadata.ogDescription} />
        )}
        {metadata.ogImage && (
          <meta name="twitter:image" content={metadata.ogImage} />
        )}
      </Helmet>
      {/* Render JSON-LD graph for SEO and rich results. Keep payload minimal and safe. */}
      <JsonLd
        siteUrl={
          (getSetting("site_url", "") ||
            (typeof window !== "undefined"
              ? window.location.origin
              : "https://minorsoccertalks.com")) as string
        }
        pathname={location.pathname}
        pageTitle={metadata.title}
        pageDescription={metadata.description}
        breadcrumb={(() => {
          // Build a small breadcrumb trail for category/topic pages
          const crumbs: { name: string; url: string }[] = [];
          if (location.pathname === "/") return crumbs;
          // Home
          crumbs.push({ name: FORUM_NAME, url: "/" });
          if (params.categorySlug) {
            crumbs.push({
              name: formatSlug(params.categorySlug),
              url: `/${params.categorySlug}`,
            });
          }
          if (params.subcategorySlug) {
            crumbs.push({
              name: formatSlug(params.subcategorySlug),
              url: `/${params.categorySlug}/${params.subcategorySlug}`,
            });
          }
          if (params.topicSlug) {
            crumbs.push({
              name: formatSlug(params.topicSlug),
              url: location.pathname,
            });
          }
          return crumbs;
        })()}
        topic={
          topicMetadata
            ? {
                id: (topicMetadata as TopicMetadataType).id,
                title: (topicMetadata as TopicMetadataType).title,
                content: (topicMetadata as TopicMetadataType).content,
                // Always provide author for schema.org
                author: (topicMetadata as any).profiles?.username
                  ? {
                      username: (topicMetadata as any).profiles.username,
                      id: (topicMetadata as any).profiles.id,
                    }
                  : { username: "Anonymous", id: null },
                // Always provide created_at for schema.org
                created_at:
                  (topicMetadata as TopicMetadataType).created_at ||
                  new Date().toISOString(),
                reply_count: (topicMetadata as TopicMetadataType).reply_count,
                // Always provide url for schema.org
                url:
                  metadata.canonical ||
                  `${
                    getSetting("site_url", "") ||
                    (typeof window !== "undefined"
                      ? window.location.origin
                      : "https://minorsoccertalks.com")
                  }${location.pathname}`,
              }
            : null
        }
      />
      {children}
    </MetadataContext.Provider>
  );
};
